import { Component, effect, inject, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { UserAdminService } from '../../../core/services/user-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { AdminUserResponse } from '../../../core/models';
import { AppDialog } from '../../../shared/ui/dialog/app-dialog';
import { generatePassword } from '../../../shared/utils/password.util';

const SYSTEM_ROLE_OPTIONS = [
  { label: 'Administrateur',         value: 'ROLE_ADMIN' },
  { label: 'Super administrateur',   value: 'ROLE_SUPER_ADMIN' },
];

// Un admin délégué (droit SYSTEM_USERS_MANAGE sans être lui-même super-admin) ne peut
// créer que des ROLE_ADMIN — le backend refuse ROLE_SUPER_ADMIN dans ce cas (protection
// anti auto-élévation). On ne propose donc pas l'option s'il ne peut pas s'en servir.
const DELEGATED_ROLE_OPTIONS = SYSTEM_ROLE_OPTIONS.filter(o => o.value === 'ROLE_ADMIN');

@Component({
  selector: 'app-system-user-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule,
    PasswordModule, SelectButtonModule, TooltipModule, AppDialog],
  templateUrl: './system-user-form.html',
  styleUrl: './system-user-form.scss'
})
export class SystemUserFormComponent {
  private svc   = inject(UserAdminService);
  private fb    = inject(FormBuilder);
  private toast = inject(MessageService);
  private auth  = inject(AuthService);

  readonly roleOptions = this.auth.isSuperAdmin() ? SYSTEM_ROLE_OPTIONS : DELEGATED_ROLE_OPTIONS;

  visible = model<boolean>(false);
  created = output<AdminUserResponse>();

  saving = signal(false);
  createError = signal('');

  form = this.fb.group({
    firstName: ['', Validators.required],
    lastName:  ['', Validators.required],
    email:     ['', [Validators.required, Validators.email]],
    phone:     ['', Validators.required],
    password:  ['', [Validators.required, Validators.minLength(8)]],
    role:      ['ROLE_ADMIN', Validators.required],
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.form.reset({ role: 'ROLE_ADMIN' });
        this.createError.set('');
      }
    });
  }

  close() {
    this.visible.set(false);
  }

  generatePassword() {
    this.form.get('password')?.setValue(generatePassword());
  }

  submitCreate() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.createError.set('');

    const v = this.form.getRawValue();
    const data = {
      firstName: v.firstName ?? '',
      lastName:  v.lastName ?? '',
      email:     v.email ?? '',
      phone:     v.phone ?? '',
      password:  v.password ?? '',
      role:      v.role ?? 'ROLE_ADMIN',
    };

    this.svc.createSystemUser(data).pipe(catchError(err => {
      this.createError.set(err?.error?.message ?? "La création du compte système a échoué.");
      this.saving.set(false);
      return of(null);
    })).subscribe(res => {
      if (res) {
        this.saving.set(false);
        this.visible.set(false);
        this.toast.add({ severity: 'success', summary: 'Compte système créé', detail: `« ${res.firstName} ${res.lastName} » a été créé.`, life: 4000 });
        this.created.emit(res);
      } else {
        this.saving.set(false);
      }
    });
  }
}
